import Button from "@ergeon/core-components/exports/Button.ts";

import Logo from "@/components/Logo";

import { Permissions, RequestPermissions } from "@/app/types";
import DevicePermissionChip from "@/app/pages/LobbyPage/DevicePermissionChip";
import LocationPermissionChip from "@/app/pages/LobbyPage/LocationPermissionChip";
import LobbyVideo from "@/app/pages/LobbyPage/LobbyVideo";
import ConsentNotice from "@/app/pages/LobbyPage/ConsentNotice";
import useLocationPermission from "@/app/hooks/useLocationPermission";


interface ILobbyPageProps {
  startTime: string;
  address: string;
  customerName?: string;
  staffName?: string;
  permissions: Permissions;
  isMicrophoneDenied: boolean;
  requestPermissions: RequestPermissions;
  videoStream?: MediaStream;
  onJoin(): void;
  isClient: boolean;
}

const SECTION_LABEL = "paragraph-3 font-bold uppercase tracking-wide text-gray-500 dark:text-gray-400";
const MUTED = "text-gray-500 dark:text-gray-400";


export default function LobbyPage(
  {
    startTime,
    isMicrophoneDenied,
    address,
    customerName,
    staffName,
    permissions,
    requestPermissions,
    videoStream,
    onJoin,
    isClient,
  }: ILobbyPageProps
) {
  const joinButtonText = isClient ? "Join & Consent" : "Join call";
  const meetingWith = isClient ? staffName : customerName;

  // Owned here rather than in the chip, because the readiness heading above the
  // button can only claim "ready" if it knows about all three permissions. Only
  // the customer walks the property, so only they are asked for location.
  const location = useLocationPermission(isClient);
  const tracksLocation = isClient && location.state !== "unsupported";
  const isReady =
    permissions.video && permissions.audio
    && (!tracksLocation || location.state === "granted");

  const joinButton = (
    <Button flavor="primary" isFullWidth onClick={onJoin} size="large">
      {joinButtonText}
    </Button>
  );

  // A customer whose microphone is off can still join — the call is simply
  // one-way until they fix it, and saying so beats a button that only looks
  // broken.
  const microphoneWarning = isMicrophoneDenied && !permissions.audio && (
    <p className="paragraph-3 text-orange-600 dark:text-orange-400 animate-in fade-in duration-300">
      Your microphone is off — turn it on so you can be heard.
    </p>
  );

  // Conditions, then readiness, then the action. The chips sit against the
  // button so the customer confirms what they are about to allow in one glance
  // instead of pairing a checklist with a button a section away.
  const joinFlow = (
    <div className="flex flex-col gap-3 md:sticky md:bottom-0 md:z-10 md:bg-white/95 md:pb-6 md:pt-4 md:backdrop-blur md:dark:bg-gray-900/95">
      <h2 className="paragraph-2 font-bold text-gray-900 dark:text-gray-100">
        {isReady ? "Ready to join" : "Almost ready"}
      </h2>
      <ul className="flex flex-wrap gap-2">
        <DevicePermissionChip
          hasPermission={permissions.video}
          permission={"video"}
          requestPermissions={requestPermissions}
        />
        <DevicePermissionChip
          hasPermission={permissions.audio}
          permission={"audio"}
          requestPermissions={requestPermissions}
        />
        {tracksLocation && <LocationPermissionChip {...location} />}
      </ul>
      {microphoneWarning}
      {/* On a phone the same button rides along the bottom of the screen. */}
      <div className="hidden md:block">{joinButton}</div>
    </div>
  );

  return (
    <div className="relative box-border flex h-max min-h-screen w-full flex-col items-center bg-white px-5 pt-6 font-default leading-normal text-gray-900 dark:bg-gray-900 dark:text-gray-100 md:px-10 lg:px-12">
      {/* One centred column holds the page. Margin auto rather than
          justify-center, so a lobby taller than the window scrolls from the top
          instead of losing its head. */}
      <div className="w-full max-w-6xl md:my-auto">
        <section className="grid w-full grid-cols-1 gap-10 pb-8 md:grid-cols-[minmax(0,1fr)_360px] lg:grid-cols-[minmax(0,1fr)_480px] lg:gap-14 xl:grid-cols-[minmax(0,1fr)_520px]">
          {/* The preview stays put while the notice and the checklist scroll
              past it: on a short window it would otherwise be the first thing
              to leave the screen, and it is what the customer came to check. */}
          <div className="hidden md:sticky md:top-6 md:flex md:flex-col md:self-start">
            <LobbyVideo videoStream={videoStream} />
          </div>

          <div className="flex w-full max-w-lg flex-col gap-7 md:max-w-none">
            <header className="flex flex-col gap-6">
              {/* Without this the flex row stretches the SVG to the column and
                  the wordmark centres itself inside its own viewBox. */}
              <Logo className="self-start" />
              <div className="flex flex-col gap-1">
                <h1 className="heading-3 text-gray-900 dark:text-gray-100">
                  Live Video Estimate
                </h1>
                {startTime && <p className="paragraph-1">{startTime}</p>}
                {meetingWith && <p className={`paragraph-2 ${MUTED}`}>with {meetingWith}</p>}
              </div>
            </header>

            <div className="md:hidden">
              <LobbyVideo videoStream={videoStream} />
            </div>

            {isClient && <ConsentNotice />}

            {joinFlow}

            {/* Reference material once the decision is made, so it carries no
                card of its own — a rule and a label are enough. */}
            <section className="flex flex-col gap-2 border-t border-gray-200 pt-6 dark:border-gray-800">
              <h2 className={SECTION_LABEL}>Appointment</h2>
              <div className="flex flex-col gap-1">
                <p className="paragraph-2">{address}</p>
                {!isClient && customerName && (
                  <p className={`paragraph-3 ${MUTED}`}>Customer · {customerName}</p>
                )}
                {staffName && (
                  <p className={`paragraph-3 ${MUTED}`}>Sales rep · {staffName}</p>
                )}
              </div>
            </section>
          </div>
        </section>
      </div>

      {/* On a phone the notice and the checklist push the only button that
          matters past the fold, so it rides along the bottom instead. */}
      <div className="sticky bottom-0 z-10 -mx-5 mt-auto self-stretch border-t border-gray-200 bg-white/95 px-5 pb-safe-bottom pt-3 backdrop-blur dark:border-gray-800 dark:bg-gray-900/95 md:hidden">
        <div className="max-w-lg">{joinButton}</div>
      </div>
    </div>
  );
}
